import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { IOrganizationService } from 'src/app/models/muli-org/organization-service.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatMultiSelectPaginationComponent } from "../mat-multiselect/mat-multiselect-pagination.component";

@Component({
  selector: 'app-org-associations',
  templateUrl: './org-associations.component.html',
  styleUrls: ['./org-associations.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatMultiSelectPaginationComponent 
  ]
})
export class OrgAssociationsComponent implements OnInit, OnDestroy {
  @Input() organizationId: string | number;
  @Input() isAdmin: boolean = false;
  @Input() isSystemUser: boolean = false;
  @Input() organizationService!: IOrganizationService;

  isEditingAssociations = false;
  isLoading = false;
  associatedOrganizations: any[] = [];
  allProviderOrganizations: any[] = [];
  parentOrganization: any = null;
  childOrganizations: any[] = [];
  hasNextPageOrganizations = true;
  currentPageOrganizations = 1;
  pageSize = 20;

  associationForm: FormGroup = new FormGroup({
    associatedOrgIds: new FormControl([])
  });

  subscriptions: Subscription[] = [];

  constructor() {}

  ngOnInit(): void {
    this.loadOrganizationRelationships();
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach((s) => s.unsubscribe());
  }

  public loadOrganizationRelationships() {
    const orgId = this.organizationId.toString();
    const sub = this.organizationService
      .getRelationships(orgId)
      .subscribe((response) => {
        if (!response.isError) {
          this.parentOrganization = response.data.parent;
          this.childOrganizations = response.data.children || [];
          this.associatedOrganizations = response.data.associations || [];
          if (this.isEditingAssociations) {
            this.currentPageOrganizations = 1;
            this.hasNextPageOrganizations = true;
            this.allProviderOrganizations = [];
            this.fetchOrganizations({ searchKey: '', reset: true });
          }
        }
      });
    this.subscriptions.push(sub);
  }

toggleEditAssociations() {
  if (!this.isEditingAssociations) {
    const orgId = this.organizationId.toString();
    const sub = this.organizationService
      .getRelationships(orgId)
      .subscribe((response) => {
        if (!response.isError) {
          this.parentOrganization = response.data.parent;
          this.childOrganizations = response.data.children || [];
          this.associatedOrganizations = response.data.associations || [];
          this.currentPageOrganizations = 1;
          this.hasNextPageOrganizations = true;
          this.allProviderOrganizations = [];
          this.fetchOrganizations({ searchKey: '', reset: true });
        }
      });
    this.subscriptions.push(sub);
  }
  this.isEditingAssociations = !this.isEditingAssociations;
}

  onLoadMoreOrganizations(event: { searchKey: string; reset?: boolean }) {
    this.fetchOrganizations(event);
  }

  private fetchOrganizations(event: { searchKey: string; reset?: boolean }) {
    const { searchKey, reset } = event;

    const params: any = {
      sortColumn: 'name',
      sortOrder: 'asc',
      page: reset ? 1 : this.currentPageOrganizations + 1,
      pageSize: this.pageSize,
      searchKey: searchKey || '',
      isActive: true
    };

    const sub = this.organizationService.get(params)
      .subscribe((response: any) => {
        if (!response.isError && response.data?.items) {
          // Filter out: current org, parent, and children
          const excludeIds = [
            parseInt(this.organizationId.toString()),
            this.parentOrganization?.id,
            ...this.childOrganizations.map(c => c.id)
          ].filter(id => id !== null && id !== undefined);

          const newOrgs = response.data.items
            .filter((org: any) => !excludeIds.includes(org.id))
            .map((org: any) => ({ id: org.id, name: org.name }));

          if (reset) {
            this.allProviderOrganizations = newOrgs;
            this.currentPageOrganizations = 1;
          } else {
            this.allProviderOrganizations = [
              ...this.allProviderOrganizations,
              ...newOrgs
            ];
            this.currentPageOrganizations = response.data.page;
          }
          this.hasNextPageOrganizations = response.data.hasNextPage;
          if (reset) {
            const selectedAssociations = this.allProviderOrganizations.filter(org => {
              return this.associatedOrganizations.some((assoc: any) => assoc.id === org.id);
            });
            this.associationForm.patchValue({
              associatedOrgIds: selectedAssociations
            });
          }
        }
      });
    this.subscriptions.push(sub);
  }
  cancelEditAssociations() {
    this.isEditingAssociations = false;
    this.associationForm.reset();
  }

  saveAssociationChanges() {
    this.isLoading = true;
    const orgId = this.organizationId.toString();

    const currentAssocIds = this.associatedOrganizations.map(a => a.id);
    const selectedIds = this.associationForm.value.associatedOrgIds.map((a: any) => a.id);

    const toLink = selectedIds.filter((id: number) => !currentAssocIds.includes(id));
    const toUnlink = currentAssocIds.filter((id: number) => !selectedIds.includes(id));
    const operations: Observable<any>[] = [];

    if (toLink.length > 0) {
      operations.push(
        this.organizationService.linkAssociative(orgId, toLink)
      );
    }
    toUnlink.forEach((assocId: number) => {
      operations.push(
        this.organizationService.unlinkAssociative(orgId, assocId.toString())
      );
    });

    if (operations.length === 0) {
      this.isLoading = false;
      this.cancelEditAssociations();
      return;
    }

    const executeOperations = (index: number) => {
      if (index >= operations.length) {
        this.isLoading = false;
        this.organizationService.clearRelationshipsCache(orgId);
        this.loadOrganizationRelationships();
        this.cancelEditAssociations();
        return;
      }

      const sub = operations[index].subscribe({
        next: () => {
          executeOperations(index + 1);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error updating associations:', error);
          this.loadOrganizationRelationships();
          this.cancelEditAssociations();
        }
      });
      this.subscriptions.push(sub);
    };

    executeOperations(0);
  }
}